import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, Clock, ShieldCheck, Star } from "lucide-react";
import { Logo } from "./Logo";
import { BOLOGNA } from "../data/content";
import { useLang } from "../i18n/LanguageContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  const { t } = useLang();
  const [index, setIndex] = useState(0);

  const next = useCallback(() => setIndex((i) => (i + 1) % BOLOGNA.length), []);

  useEffect(() => {
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [next]);

  const features = [
    { icon: ShieldCheck, label: t.hero.f1 },
    { icon: Clock, label: t.hero.f2 },
    { icon: Star, label: t.hero.f3 },
  ];

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-black"
    >
      {/* Slideshow di Bologna come sfondo */}
      <AnimatePresence mode="sync">
        <motion.img
          key={index}
          src={BOLOGNA[index].img}
          alt={`Bologna - ${BOLOGNA[index].label}`}
          data-testid={`slideshow-image-${index}`}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1, transition: { duration: 1.6, ease: "easeInOut" } }}
          exit={{ opacity: 0, transition: { duration: 1.6, ease: "easeInOut" } }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-black/5" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-28 pb-24">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <Logo className="h-16 md:h-20 w-auto text-[#C5A059]" strokeWidth={2.4} animated />
          </motion.div>

          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="block text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6"
          >
            {t.hero.overline}
          </motion.span>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.45 }}
            className="font-serif text-xl sm:text-2xl lg:text-3xl tracking-tight leading-snug text-white max-w-xl"
          >
            {t.hero.taglinePre}
            <span className="italic font-light">{t.hero.taglineBold}</span>
            {t.hero.taglinePost}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.65 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={() => scrollTo("preventivo")}
              data-testid="hero-cta"
              className="px-8 py-4 bg-[#C5A059] text-[#0A0A0C] text-sm font-medium uppercase tracking-[0.15em] hover:bg-[#d8b877] transition-colors"
            >
              {t.hero.ctaBook}
            </button>
            <button
              onClick={() => scrollTo("flotta")}
              data-testid="hero-fleet-btn"
              className="px-8 py-4 border border-white/30 text-white text-sm font-medium uppercase tracking-[0.15em] hover:bg-white/10 transition-colors"
            >
              {t.hero.ctaFleet}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-4"
          >
            {features.map((f, i) => (
              <div key={i} className="flex items-center gap-2.5 text-zinc-300">
                <f.icon className="h-4 w-4 text-white" />
                <span className="text-sm">{f.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Didascalia città + indicatori (in basso a sinistra per non sovrapporsi al badge) */}
      <div className="absolute bottom-8 left-6 md:bottom-10 md:left-12 z-30 flex items-center gap-4">
        <div className="flex gap-2.5">
          {BOLOGNA.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              data-testid={`slideshow-dot-${i}`}
              aria-label={`Slide ${i + 1}`}
              className={`h-2 rounded-full transition-all duration-500 ${
                i === index ? "w-8 bg-[#C5A059]" : "w-2 bg-white/50 hover:bg-white/80"
              }`}
            />
          ))}
        </div>
        <span
          data-testid="slideshow-caption"
          className="hidden sm:block text-xs uppercase tracking-[0.25em] text-zinc-200"
        >
          {BOLOGNA[index].label}
        </span>
      </div>

      <button
        onClick={() => scrollTo("servizi")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-zinc-400 hover:text-white transition-colors z-20"
        aria-label="Scroll"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </button>
    </section>
  );
};
